import { useApi, type MyChildResponse, type Presence } from '@daghfal/core';
import { useQuery } from '@tanstack/react-query';

/** The one cache key for `/me/children`, so the chooser, the shell and the day screen share one copy. */
export const MY_CHILDREN_KEY = ['me', 'children'] as const;

/** The guardian's children, as the chooser lists them. */
export function useMyChildren() {
  const api = useApi();
  return useQuery({ queryKey: MY_CHILDREN_KEY, queryFn: () => api.guardians.myChildren() });
}

/**
 * One of the guardian's children, from the same cached list the chooser reads. A stale copy is refetched on mount,
 * so a screen opened from a notification still sees a changed history window or a withdrawal (Q28, Q32).
 */
export function useMyChild(childId: number): MyChildResponse | undefined {
  return useMyChildren().data?.find((child) => child.childId === childId);
}

/**
 * Today's presence as the presence label draws it, for the chooser and the child's home alike.
 *
 * "Expected" becomes "not arrived yet" rather than "absent", which is the distinction the absence rule
 * exists to make: a parent looking at this at nine in the morning must not be told their child is absent.
 */
export function presenceOf(child: MyChildResponse): Presence {
  switch (child.attendance) {
    case 'Present': return { kind: 'present' };
    // Departed is decided by the check-out, so the server sends its time with it.
    case 'Departed': return { kind: 'departed', at: child.departedAt ?? '' };
    case 'NotifiedAbsence': return { kind: 'absenceReported' };
    default: return { kind: 'notArrived' };
  }
}

/** Where a child opens: the payments screen alone for a child who has left with a balance due (Q32). */
export function childHome(child: MyChildResponse): string {
  return child.paymentsOnly ? `/child/${child.childId}/payments` : `/child/${child.childId}`;
}
